import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaLayerGroup,
  FaTags,
  FaCss3,
  FaExchangeAlt,
  FaExclamationTriangle,
  FaSquare,
  FaMinus,
  FaAlignLeft
} from 'react-icons/fa';

const HTMLLesson11 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const code1 = `<div class="block-demo">
  <div>London</div>
  <div>Paris</div>
  <div>Tokyo</div>
</div>

<style>
.block-demo div {
  background: #3b82f6;
  color: white;
  padding: 1rem;
  margin: 0.5rem 0;
  border-radius: 4px;
}
</style>`;

  const code2 = `<p>This is a paragraph with 
  <span class="inline-demo">inline elements</span> 
  like <span class="inline-demo">spans</span> 
  and <strong>strong tags</strong> 
  and <em>emphasis tags</em>.
</p>

<style>
.inline-demo {
  background: #10b981;
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 3px;
}
</style>`;

  const code3 = `<!-- Block elements create structure -->
<header>
  <h1>Page Title</h1>
  <nav>Navigation Menu</nav>
</header>

<main>
  <article>
    <h2>Article Title</h2>
    <p>Article content...</p>
  </article>
  
  <aside>
    <h3>Sidebar</h3>
    <p>Additional content</p>
  </aside>
</main>

<footer>
  <p>Footer content</p>
</footer>`;

  const code4 = `<!-- Inline elements for text-level semantics -->
<p>
  You can <strong>emphasize important text</strong>, 
  add <a href="#links">links</a>, 
  use <code>code snippets</code>, 
  and create <em>italic text</em> 
  without breaking the flow.
</p>

<!-- Mixed usage -->
<div class="card">
  <h2>Product <span class="badge">New</span></h2>
  <p>Price: <strong>$99</strong> <em>(limited time)</em></p>
  <button>Add to Cart</button>
</div>`;

  const code5 = `/* Changing display properties */
.block-to-inline {
  display: inline;
  background: #ef4444;
  padding: 0.5rem;
}

.inline-to-block {
  display: block;
  background: #8b5cf6;
  padding: 1rem;
  margin: 0.5rem 0;
}

.inline-block-demo {
  display: inline-block;
  width: 200px;
  background: #f59e0b;
  padding: 1rem;
  margin: 0.5rem;
}

.flex-container {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: #f3f4f6;
}`;

  const code6 = `<!DOCTYPE html>
<html>
<head>
  <style>
    /* Block-level styling */
    header, nav, main, footer, section, article, aside {
      border: 2px solid #e5e7eb;
      margin: 1rem 0;
      padding: 1rem;
    }
    
    header { background: #dbeafe; border-color: #3b82f6; }
    nav { background: #f3f4f6; border-color: #6b7280; }
    main { background: #f0fdf4; border-color: #22c55e; }
    footer { background: #fef3c7; border-color: #f59e0b; }
    
    /* Inline element styling */
    .highlight { background: #fef3c7; padding: 0.2rem 0.4rem; }
    .keyword { color: #dc2626; font-weight: bold; }
    .link { color: #2563eb; text-decoration: underline; }
    
    /* Display property examples */
    .inline-list {
      display: inline;
      margin: 0 0.5rem;
      padding: 0.25rem 0.5rem;
      background: #e5e7eb;
    }
  </style>
</head>
<body>
  <!-- Block-level structure -->
  <header>
    <h1>Website <span class="highlight">Header</span></h1>
    <p>This entire header is a <strong class="keyword">block element</strong></p>
  </header>
  
  <nav>
    Navigation: 
    <span class="inline-list">Home</span>
    <span class="inline-list">About</span>
    <span class="inline-list">Contact</span>
  </nav>
  
  <main>
    <article>
      <h2>Main <em>Content</em> Area</h2>
      <p>This paragraph contains <span class="highlight">inline elements</span> 
      like <strong>strong text</strong>, <em>italic text</em>, and 
      <a href="#" class="link">clickable links</a>.</p>
    </article>
    
    <aside>
      <h3>Sidebar <span class="highlight">Info</span></h3>
      <p>Sidebar content with <code>inline code</code> examples.</p>
    </aside>
  </main>
  
  <footer>
    <p>Footer content with <strong>important notices</strong> and 
    <em>additional information</em>.</p>
  </footer>
</body>
</html>`;

  const sections = [
    {
      title: "Block vs Inline Elements Introduction",
      content: "Understanding the fundamental difference between block and inline elements is crucial for proper HTML structure and layout",
      keyPoints: [
        "Block elements start on a new line and take full width",
        "Inline elements flow within text and take only necessary width",
        "Block elements can contain other block and inline elements",
        "Inline elements cannot contain block elements"
      ],
      icon: <FaAlignLeft className="section-icon" />,
      color: "#2563eb",
      code: code1
    },
    {
      title: "Block-level Elements Deep Dive", 
      content: "Block elements create the main structure of your webpage and always start on a new line",
      keyPoints: [
        "Common block elements: div, p, h1-h6, header, footer, section",
        "Take full available width by default",
        "Can have margins, padding, width, and height set",
        "Used for major layout sections and content containers"
      ],
      icon: <FaSquare className="section-icon" />,
      color: "#1d4ed8",
      code: code3
    },
    {
      title: "Inline Elements Explained",
      content: "Inline elements are used within text content to add semantic meaning and styling without breaking flow",
      keyPoints: [
        "Common inline elements: span, a, strong, em, img, code",
        "Only take necessary width for content",
        "Cannot have width/height set (by default)",
        "Perfect for text-level semantics and styling"
      ],
      icon: <FaMinus className="section-icon" />,
      color: "#1e40af",
      code: code4
    },
    {
      title: "Changing Display Properties",
      content: "CSS display property allows you to change how elements behave, creating flexible layouts",
      keyPoints: [
        "display: inline - makes block elements behave like inline",
        "display: block - makes inline elements behave like blocks",
        "display: inline-block - hybrid with width/height support",
        "Modern options: flex, grid, none for advanced layouts"
      ],
      icon: <FaExchangeAlt className="section-icon" />,
      color: "#7c3aed",
      code: code5
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 3;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 11 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">Block vs Inline Elements</h1>
              <p className="html-lesson-subtitle">
                Master HTML element display types to create proper document structure and layouts
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title.split(' ')[0]}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Visual Demonstration */}
          <div 
            className="html-visual-class"
            ref={(el) => addSectionRef(el, 0)}
          >
            <div className="html-visual-header">
              <div className="html-code-badge">
                <FaLayerGroup className="html-badge-icon" />
                Display Types Comparison
              </div>
              <h3 className="html-visual-title">
                Visualizing Block vs Inline Element Behavior
              </h3>
            </div>
            
            <div className="html-class-demo">
              <div className="html-demo-code">
                <h4>Block Elements</h4>
                <div className="html-code-block-small">
                  <CopyBlock
                    language="html"
                    text={`<div>Block Element 1</div>
<div>Block Element 2</div>
<div>Block Element 3</div>`}
                    showLineNumbers={false}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
                <div className="html-block-preview">
                  <div className="block-element">Block Element 1</div>
                  <div className="block-element">Block Element 2</div>
                  <div className="block-element">Block Element 3</div>
                </div>
              </div>
              
              <div className="html-demo-visual">
                <h4>Inline Elements</h4>
                <div className="html-code-block-small">
                  <CopyBlock
                    language="html"
                    text={`<span>Inline 1</span>
<span>Inline 2</span>
<span>Inline 3</span>`}
                    showLineNumbers={false}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
                <div className="html-inline-preview">
                  <span className="inline-element">Inline 1</span>
                  <span className="inline-element">Inline 2</span>
                  <span className="inline-element">Inline 3</span>
                </div>
              </div>
            </div>
            
            <div className="html-demo-note">
              <FaExclamationTriangle className="html-note-icon" />
              <p><strong>Key Difference:</strong> Block elements stack vertically, inline elements flow horizontally within text content.</p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index + 1)}
                className={`html-content-card ${viewedSections.has(index + 1) ? 'html-viewed' : ''} ${(index + 1) === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index + 1) ? '✓ Viewed' : `Part ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content">{section.content}</p>
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example */}
                {section.code && (
                  <div className="html-section-code">
                    <div className="html-code-badge">
                      <FaCode className="html-badge-icon" />
                      Example
                    </div>
                    <div className="html-code-block-small">
                      <CopyBlock
                        language={language}
                        text={section.code}
                        showLineNumbers={lineNumbers}
                        theme={dracula}
                        wrapLines={true}
                        codeBlock
                      />
                    </div>
                  </div>
                )}
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Block vs Inline Comparison */}
          <div 
            className="html-comparison-section"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-comparison-header">
              <div className="html-code-badge">
                <FaExchangeAlt className="html-badge-icon" />
                Block vs Inline Comparison
              </div>
              <h3 className="html-comparison-title">
                When to Use Block vs Inline Elements
              </h3>
            </div>
            
            <div className="html-comparison-table">
              <table>
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Block Elements</th>
                    <th>Inline Elements</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Line Break</strong></td>
                    <td>✅ Always starts on new line</td>
                    <td>❌ No line break</td>
                  </tr>
                  <tr>
                    <td><strong>Width</strong></td>
                    <td>Full available width</td>
                    <td>Only necessary width</td>
                  </tr>
                  <tr>
                    <td><strong>Height/Width</strong></td>
                    <td>✅ Can be set</td>
                    <td>❌ Cannot be set (by default)</td>
                  </tr>
                  <tr>
                    <td><strong>Margins/Padding</strong></td>
                    <td>✅ All sides work</td>
                    <td>✅ Horizontal only (vertical affects line height)</td>
                  </tr>
                  <tr>
                    <td><strong>Content Rules</strong></td>
                    <td>Can contain other blocks and inlines</td>
                    <td>Cannot contain block elements</td>
                  </tr>
                  <tr>
                    <td><strong>Best For</strong></td>
                    <td>Layout structure, sections</td>
                    <td>Text content, semantic markup</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Common Elements Section */}
          <div 
            className="html-practices-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-practices-header">
              <div className="html-code-badge">
                <FaCheck className="html-badge-icon" />
                Common HTML Elements
              </div>
              <h3 className="html-practices-title">
                Frequently Used Block and Inline Elements
              </h3>
            </div>
            
            <div className="html-practices-grid">
              <div className="html-practice-card html-do">
                <h4>📦 Common Block Elements</h4>
                <ul>
                  <li><code>&lt;div&gt;</code> - Generic container</li>
                  <li><code>&lt;p&gt;</code> - Paragraph</li>
                  <li><code>&lt;h1&gt;-&lt;h6&gt;</code> - Headings</li>
                  <li><code>&lt;header&gt;</code> - Header section</li>
                  <li><code>&lt;footer&gt;</code> - Footer section</li>
                  <li><code>&lt;section&gt;</code> - Document section</li>
                  <li><code>&lt;article&gt;</code> - Independent content</li>
                  <li><code>&lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</code> - Lists</li>
                </ul>
              </div>
              
              <div className="html-practice-card html-dont">
                <h4>🔗 Common Inline Elements</h4>
                <ul>
                  <li><code>&lt;span&gt;</code> - Generic inline container</li>
                  <li><code>&lt;a&gt;</code> - Hyperlink</li>
                  <li><code>&lt;strong&gt;</code> - Important text</li>
                  <li><code>&lt;em&gt;</code> - Emphasized text</li>
                  <li><code>&lt;img&gt;</code> - Image</li>
                  <li><code>&lt;code&gt;</code> - Code snippet</li>
                  <li><code>&lt;br&gt;</code> - Line break</li>
                  <li><code>&lt;button&gt;</code> - Clickable button</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Practical Example */}
          <div 
            className="html-multiple-classes"
            ref={(el) => addSectionRef(el, sections.length + 3)}
          >
            <div className="html-multiple-header">
              <div className="html-code-badge">
                <FaTags className="html-badge-icon" />
                Practical Usage
              </div>
              <h3 className="html-multiple-title">
                Combining Block and Inline Elements in Real Projects
              </h3>
            </div>
            
            <div className="html-multiple-content">
              <div className="html-multiple-code">
                <CopyBlock
                  language={language}
                  text={code6}
                  showLineNumbers={lineNumbers}
                  theme={dracula}
                  wrapLines={true}
                  codeBlock
                />
              </div>
              
              <div className="html-multiple-explanation">
                <h4>Structure Breakdown:</h4>
                <ul>
                  <li><strong>Block Elements:</strong> <code>header, nav, main, article, aside, footer</code> create page structure</li>
                  <li><strong>Inline Elements:</strong> <code>span, strong, em, code, a</code> style text content</li>
                  <li><strong>Mixed Usage:</strong> Block elements contain inline elements for rich content</li>
                  <li><strong>CSS Display:</strong> Using <code>display: inline</code> to change element behavior</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 4)}
          >
            <div className="html-action-header">
              <h3>Mastered block and inline elements?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned the fundamental difference between block and inline elements, when to use each, and how they create proper HTML structure. Start building well-structured web pages!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-images" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more examples? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson11;